import type { DocumentFile, FormDefinition } from './api.types';

export type FormSubmission = {
  title: string;
  data: Record<string, unknown>;
};

export type FormPreviewDetails = {
  formTitle: string;
  uniqueKey: string;
};

export function formValuesHaveChanges(
  current: Record<string, unknown>,
  initial: Record<string, unknown>,
): boolean {
  const keys = new Set([...Object.keys(current), ...Object.keys(initial)]);
  for (const key of keys) {
    if (!sameFormValue(current[key], initial[key])) return true;
  }
  return false;
}

export function isFormSubmissionFile(file: DocumentFile): boolean {
  const fileName = file.fileName.toLowerCase();
  const contentType = (file.contentType ?? '').toLowerCase();
  return fileName.startsWith('form-') &&
    (fileName.endsWith('.json') || contentType.includes('json'));
}

export function parseFormSubmission(text: string, invalidMessage: string): FormSubmission {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new Error(invalidMessage);
  }

  if (!isRecord(parsed)) {
    throw new Error(invalidMessage);
  }

  if (typeof parsed.form === 'string' && isRecord(parsed.data)) {
    return { title: parsed.form, data: { ...parsed.data } };
  }

  // Read submissions written by the previous verbose format as well.
  const legacyFormTitle = typeof parsed.formTitle === 'string'
    ? parsed.formTitle
    : isRecord(parsed.form) && typeof parsed.form.title === 'string'
      ? parsed.form.title
      : typeof parsed.form === 'string'
        ? parsed.form
        : null;
  if (legacyFormTitle && Array.isArray(parsed.fields)) {
    const data: Record<string, unknown> = {};
    for (const field of parsed.fields) {
      if (isRecord(field) && typeof field.name === 'string' && 'value' in field) {
        const value = field.value;
        data[field.name] = isRecord(value) && typeof value.name === 'string'
          ? value.name
          : value;
      }
    }
    return { title: legacyFormTitle, data };
  }

  throw new Error(invalidMessage);
}

export function autoTagNamesForFormData(
  form: FormDefinition,
  data: Record<string, unknown>,
): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const field of form.fields) {
    if (field.dataType !== 'tagSelect' || !field.addTagToDocument) continue;
    const dataKey = Object.keys(data).find(key => key === field.name)
      ?? Object.keys(data).find(key =>
        key.toLocaleLowerCase() === field.name.toLocaleLowerCase());
    const value = dataKey === undefined ? undefined : data[dataKey];
    if (typeof value !== 'string' || value.trim().length === 0) continue;
    const name = value.trim();
    const key = name.toLocaleLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names;
}

export function formPreviewDetails(
  form: FormDefinition,
  data: Record<string, unknown>,
): FormPreviewDetails {
  const uniqueFields = form.fields
    .filter(field => field.uniqueForDocument)
    .sort((a, b) => a.position - b.position);

  if (uniqueFields.length === 0) {
    return { formTitle: form.title, uniqueKey: '' };
  }

  const values = uniqueFields.map(field => {
    const dataKey = Object.keys(data).find(key => key === field.name)
      ?? Object.keys(data).find(key =>
        key.toLocaleLowerCase() === field.name.toLocaleLowerCase());
    return dataKey === undefined ? undefined : displayFormValue(data[dataKey]);
  });

  if (values.some(value => value === undefined)) {
    return { formTitle: form.title, uniqueKey: '' };
  }

  return {
    formTitle: form.title,
    uniqueKey: uniqueFields
      .map((field, index) => `${field.name}: ${values[index]}`)
      .join(' · '),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function displayFormValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
}

function sameFormValue(left: unknown, right: unknown): boolean {
  const normalizedLeft = left === '' || left === null || left === undefined ? null : left;
  const normalizedRight = right === '' || right === null || right === undefined ? null : right;
  if (Object.is(normalizedLeft, normalizedRight)) return true;

  if (typeof normalizedLeft !== 'object' || typeof normalizedRight !== 'object' ||
      normalizedLeft === null || normalizedRight === null) {
    return false;
  }

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}
